import React, { useState } from "react";

import { Info } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Separator } from "~/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";

interface DocumentDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  netUnitValue: number;
  netAmount: number;
  variant: {
    description: string;
    code: string;
  };
}

export type AdjustmentMethod =
  | "total_credit"
  | "per_unit_discount"
  | "quantity_removal";

export interface AdministrativeCreditItem {
  document_detail_id: number;
  original_unit_value: number;
  adjustment_amount: number; // Interpretation depends on method
  new_unit_value: number;
  description: string;
  method: AdjustmentMethod;
  affected_quantity?: number; // For quantity removal method
}

interface AdministrativeCreditNoteItemSelectorProps {
  documentDetails: DocumentDetail[];
  selectedItems: AdministrativeCreditItem[];
  onItemsChange: (items: AdministrativeCreditItem[]) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  disabled?: boolean;
}

export const AdministrativeCreditNoteItemSelector: React.FC<
  AdministrativeCreditNoteItemSelectorProps
> = ({
  documentDetails,
  selectedItems,
  onItemsChange,
  reason,
  onReasonChange,
  disabled = false,
}) => {
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editingMethod, setEditingMethod] =
    useState<AdjustmentMethod>("total_credit");
  const [editingValue, setEditingValue] = useState<string>("");

  const handleAdjustmentStart = (detailId: number) => {
    const existingItem = selectedItems.find(
      (item) => item.document_detail_id === detailId
    );
    if (existingItem) {
      setEditingMethod(existingItem.method);
      // Set the editing value based on the method
      if (existingItem.method === "total_credit") {
        const detail = documentDetails.find((d) => d.id === detailId);
        setEditingValue(
          (existingItem.adjustment_amount * (detail?.quantity || 1)).toString()
        );
      } else if (existingItem.method === "quantity_removal") {
        setEditingValue((existingItem.affected_quantity || 1).toString());
      } else {
        setEditingValue(existingItem.adjustment_amount.toString());
      }
    } else {
      setEditingMethod("total_credit");
      setEditingValue("");
    }
    setEditingItem(detailId);
  };

  const handleAdjustmentSave = () => {
    if (editingItem === null) return;

    const detail = documentDetails.find((d) => d.id === editingItem);
    if (!detail) return;

    const inputValue = parseFloat(editingValue) || 0;

    if (inputValue === 0) {
      // Remove item if value is 0
      onItemsChange(
        selectedItems.filter((item) => item.document_detail_id !== editingItem)
      );
    } else {
      let adjustmentAmount = 0;
      let newUnitValue = detail.netUnitValue;
      let affectedQuantity = detail.quantity;

      switch (editingMethod) {
        case "total_credit":
          // User enters total credit amount, calculate per-unit discount
          adjustmentAmount = inputValue / detail.quantity;
          newUnitValue = detail.netUnitValue - adjustmentAmount;
          break;

        case "per_unit_discount":
          // User enters discount per unit
          adjustmentAmount = inputValue;
          newUnitValue = detail.netUnitValue - adjustmentAmount;
          break;

        case "quantity_removal":
          // User enters how many units to remove entirely
          affectedQuantity = Math.min(inputValue, detail.quantity);
          adjustmentAmount = detail.netUnitValue; // Full unit value for removed quantity
          newUnitValue = 0; // Not applicable for quantity removal
          break;
      }

      if (newUnitValue < 0 && editingMethod !== "quantity_removal") {
        // Prevent negative unit values for discount methods
        return;
      }

      const existingIndex = selectedItems.findIndex(
        (item) => item.document_detail_id === editingItem
      );

      const newItem: AdministrativeCreditItem = {
        document_detail_id: editingItem,
        original_unit_value: detail.netUnitValue,
        adjustment_amount: adjustmentAmount,
        new_unit_value: newUnitValue,
        description: detail.variant.description,
        method: editingMethod,
        affected_quantity:
          editingMethod === "quantity_removal" ? affectedQuantity : undefined,
      };

      if (existingIndex >= 0) {
        const updated = [...selectedItems];
        updated[existingIndex] = newItem;
        onItemsChange(updated);
      } else {
        onItemsChange([...selectedItems, newItem]);
      }
    }

    setEditingItem(null);
    setEditingValue("");
  };

  const getSelectedItem = (
    detailId: number
  ): AdministrativeCreditItem | undefined => {
    return selectedItems.find((item) => item.document_detail_id === detailId);
  };

  const getTotalCreditAmount = (): number => {
    return selectedItems.reduce((total, item) => {
      const detail = documentDetails.find(
        (d) => d.id === item.document_detail_id
      );
      if (!detail) return total;

      if (item.method === "quantity_removal") {
        return total + item.adjustment_amount * (item.affected_quantity || 0);
      } else {
        return total + item.adjustment_amount * detail.quantity;
      }
    }, 0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumberInput = (amount: number): string => {
    return amount === 0 ? "" : amount.toString();
  };

  const handleAmountInputChange = (value: string): number => {
    const cleanValue = value.replace(/[^\d]/g, "");
    return cleanValue === "" ? 0 : Number(cleanValue);
  };

  const getMethodLabel = (method: AdjustmentMethod): string => {
    switch (method) {
      case "total_credit":
        return "Crédito Total";
      case "per_unit_discount":
        return "Descuento por Unidad";
      case "quantity_removal":
        return "Remoción de Cantidad";
      default:
        return "";
    }
  };

  const getAdjustmentDisplay = (
    item: AdministrativeCreditItem,
    detail: DocumentDetail
  ): string => {
    switch (item.method) {
      case "total_credit":
        return formatCurrency(item.adjustment_amount * detail.quantity);
      case "per_unit_discount":
        return formatCurrency(item.adjustment_amount);
      case "quantity_removal":
        return `${item.affected_quantity} unidades`;
      default:
        return "-";
    }
  };

  const getCreditTotalForItem = (
    item: AdministrativeCreditItem,
    detail: DocumentDetail
  ): number => {
    if (item.method === "quantity_removal") {
      return item.adjustment_amount * (item.affected_quantity || 0);
    } else {
      return item.adjustment_amount * detail.quantity;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ajustes Administrativos por Producto</CardTitle>
        <CardDescription>
          Selecciona los productos y especifica el tipo y monto de ajuste para
          cada uno
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Reason field */}
          <div className="space-y-2">
            <Label htmlFor="admin-reason">
              Razón Administrativa <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="admin-reason"
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Especifica la razón para estos ajustes administrativos (ej: Descuento por fidelidad, Ajuste por error de facturación, etc.)"
              rows={3}
              disabled={disabled}
            />
          </div>

          {/* Products table */}
          <div className="space-y-2">
            <Label>Productos Disponibles</Label>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">
                      Precio Original
                    </TableHead>
                    <TableHead className="text-right">Ajuste</TableHead>
                    <TableHead className="text-right">Nuevo Precio</TableHead>
                    <TableHead className="text-right">Crédito Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentDetails.map((detail, index) => {
                    const selectedItem = getSelectedItem(detail.id);
                    const totalCredit = selectedItem
                      ? getCreditTotalForItem(selectedItem, detail)
                      : 0;
                    const isEditing = editingItem === detail.id;

                    return (
                      <TableRow
                        key={detail.id}
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {detail.variant.description}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Código: {detail.variant.code}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {detail.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(detail.netUnitValue)}
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="min-w-[200px] space-y-3">
                              {/* Method selection */}
                              <RadioGroup
                                value={editingMethod}
                                onValueChange={(value) =>
                                  setEditingMethod(value as AdjustmentMethod)
                                }
                                className="space-y-1"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value="total_credit"
                                    id="total_credit"
                                  />
                                  <Label
                                    htmlFor="total_credit"
                                    className="text-xs"
                                  >
                                    Crédito Total
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value="per_unit_discount"
                                    id="per_unit_discount"
                                  />
                                  <Label
                                    htmlFor="per_unit_discount"
                                    className="text-xs"
                                  >
                                    $/Unidad
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value="quantity_removal"
                                    id="quantity_removal"
                                  />
                                  <Label
                                    htmlFor="quantity_removal"
                                    className="text-xs"
                                  >
                                    Remover Qty
                                  </Label>
                                </div>
                              </RadioGroup>

                              {/* Value input */}
                              <div className="flex items-center space-x-2">
                                {editingMethod !== "quantity_removal" && (
                                  <span className="text-xs text-muted-foreground">
                                    $
                                  </span>
                                )}
                                <Input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) =>
                                    setEditingValue(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleAdjustmentSave();
                                    } else if (e.key === "Escape") {
                                      setEditingItem(null);
                                    }
                                  }}
                                  className="w-20 text-right text-xs"
                                  placeholder={
                                    editingMethod === "quantity_removal"
                                      ? "Qty"
                                      : "0"
                                  }
                                  disabled={disabled}
                                  autoFocus
                                />
                              </div>

                              {/* Action buttons */}
                              <div className="flex space-x-1">
                                <Button
                                  size="sm"
                                  onClick={handleAdjustmentSave}
                                  className="h-6 px-2 text-xs"
                                >
                                  ✓
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEditingItem(null)}
                                  className="h-6 px-2 text-xs"
                                >
                                  ✕
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAdjustmentStart(detail.id)}
                              disabled={disabled}
                              className="h-8 px-2"
                            >
                              {selectedItem
                                ? getAdjustmentDisplay(selectedItem, detail)
                                : "Ajustar"}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {selectedItem ? (
                            <div>
                              {selectedItem.method === "quantity_removal" ? (
                                <span className="text-xs text-orange-600">
                                  {selectedItem.affected_quantity} removidas
                                </span>
                              ) : (
                                <span className="text-green-700">
                                  {formatCurrency(selectedItem.new_unit_value)}
                                </span>
                              )}
                              <div className="text-xs text-muted-foreground">
                                {getMethodLabel(selectedItem.method)}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">
                              {formatCurrency(detail.netUnitValue)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {totalCredit > 0 ? (
                            <span className="font-medium text-green-700">
                              {formatCurrency(totalCredit)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Summary */}
          {selectedItems.length > 0 && (
            <>
              <Separator />
              <div className="rounded-lg border bg-muted/40 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="font-medium text-foreground">
                      Resumen de Ajustes Administrativos
                    </h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {selectedItems.length} producto(s) con ajustes
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-foreground">
                      {formatCurrency(getTotalCreditAmount())}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Crédito total
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Important notes */}
          <div className="rounded-lg border bg-muted/40 p-4 text-sm">
            <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
              Métodos de Ajuste
            </div>
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              <li>
                <strong className="font-medium text-foreground">
                  Crédito Total:
                </strong>{" "}
                Ingresa el monto total a acreditar para este producto
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  $/Unidad:
                </strong>{" "}
                Ingresa el descuento por unidad individual
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Remover Qty:
                </strong>{" "}
                Especifica cuántas unidades remover completamente
              </li>
              <li>Los ajustes no afectan el stock de productos</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
